import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type OnlineImportMode,
  type OnlineOverride,
  type OnlineTextMark,
  type PatchOnlineQuestionBody,
  type SaveOnlineQuestionsBody,
  onlineTestsApi,
} from '../online';

import { classTestsKeys } from './use-class-tests';

export const onlineTestsKeys = {
  questions: (id: string) => [...classTestsKeys.all, 'online', id, 'questions'] as const,
  aiPrompt: (id: string) => [...classTestsKeys.all, 'online', id, 'ai-prompt'] as const,
  live: (id: string) => [...classTestsKeys.all, 'online', id, 'live'] as const,
  textAnswers: (id: string) => [...classTestsKeys.all, 'online', id, 'text-answers'] as const,
  review: (id: string, studentId: string) => [...classTestsKeys.all, 'online', id, 'review', studentId] as const,
};

export const ONLINE_LIVE_REFRESH_MS = 10_000;

/** Polls while the tab is visible; React Query pauses an interval in a background tab. */
export function useOnlineLiveQuery(id: string | undefined) {
  return useQuery({
    queryKey: onlineTestsKeys.live(id ?? ''),
    queryFn: () => onlineTestsApi.live(id as string),
    enabled: Boolean(id),
    refetchInterval: ONLINE_LIVE_REFRESH_MS,
  });
}

/** `live`: the test is open, so answers keep arriving — poll, and refetch on window focus. */
export function useOnlineTextAnswersQuery(id: string | undefined, { live = false }: { live?: boolean } = {}) {
  return useQuery({
    queryKey: onlineTestsKeys.textAnswers(id ?? ''),
    queryFn: () => onlineTestsApi.textAnswers(id as string),
    enabled: Boolean(id),
    refetchOnMount: 'always',
    refetchInterval: live ? ONLINE_LIVE_REFRESH_MS : false,
    refetchOnWindowFocus: live,
  });
}

export function useOnlineReviewQuery(id: string | undefined, studentId: string | undefined) {
  return useQuery({
    queryKey: onlineTestsKeys.review(id ?? '', studentId ?? ''),
    queryFn: () => onlineTestsApi.review(id as string, studentId as string),
    enabled: Boolean(id && studentId),
    refetchOnMount: 'always',
  });
}

export function useOnlineQuestionsQuery(id: string | undefined) {
  return useQuery({
    queryKey: onlineTestsKeys.questions(id ?? ''),
    queryFn: () => onlineTestsApi.questions(id as string),
    enabled: Boolean(id),
    // The editor seeds from this once (see useFreshData), so a cached copy is refetched first.
    refetchOnMount: 'always',
  });
}

/** Fetched with the page, so the copy runs inside the click: Safari refuses clipboard writes after an await. */
export function useOnlineAiPromptQuery(id: string | undefined) {
  return useQuery({
    queryKey: onlineTestsKeys.aiPrompt(id ?? ''),
    queryFn: () => onlineTestsApi.aiPrompt(id as string),
    enabled: Boolean(id),
    staleTime: Infinity,
  });
}

// A question change moves the test's maximum in the lists and the mark sheet.
function useInvalidateAll() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: classTestsKeys.all });
}

export function useSaveOnlineQuestionsMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SaveOnlineQuestionsBody }) => onlineTestsApi.saveQuestions(id, body),
    onSuccess,
  });
}

export function usePatchOnlineQuestionMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, questionId, body }: { id: string; questionId: string; body: PatchOnlineQuestionBody }) =>
      onlineTestsApi.patchQuestion(id, questionId, body),
    onSuccess,
  });
}

export function useImportOnlineQuestionsMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, csv, mode }: { id: string; csv: string; mode: OnlineImportMode }) =>
      onlineTestsApi.importCsv(id, csv, mode),
    onSuccess,
  });
}

export function useOpenOnlineTestMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({ mutationFn: (id: string) => onlineTestsApi.open(id), onSuccess });
}

export function useCloseOnlineTestMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({ mutationFn: (id: string) => onlineTestsApi.close(id), onSuccess });
}

export function useReopenOnlineTestMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({ mutationFn: (id: string) => onlineTestsApi.reopen(id), onSuccess });
}

export function useReopenOnlineStudentMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, studentId }: { id: string; studentId: string }) => onlineTestsApi.reopenStudent(id, studentId),
    onSuccess,
  });
}

// A mark can complete a student's result, so the mark sheet and the lists refresh too.
export function useSaveOnlineTextMarksMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, marks }: { id: string; marks: OnlineTextMark[] }) => onlineTestsApi.saveTextMarks(id, marks),
    onSuccess,
  });
}

export function useSaveOnlineOverridesMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, studentId, items }: { id: string; studentId: string; items: OnlineOverride[] }) =>
      onlineTestsApi.saveOverrides(id, studentId, items),
    onSuccess,
  });
}
